'use client';

import { getCourseFormErrorField } from './course-form-values';

import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import React, { useEffect } from 'react';
import { AddCourseStepper } from './add-course-stepper';
import { AddCourseStep0 } from './steps/add-course-step-0';
import { AddCourseControls } from './add-course-controls';
import { AddCourseStepsBuckets } from './steps/add-course-steps-validator';
import { deepGet } from '@eliadmin/core/src/utils/misc-utils';
import { createCourse } from '@eliadmin/core/src/api/courses/jobportal-courses-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { PortalContentLoader } from '../../profile/portal-content-loader';
import { useProfileNavigation } from '../../profile/profile-navigation';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import {
  buildCourseTextFields,
  COURSE_EXTERNAL_URL_ERROR,
  normalizeCourseExternalUrlInput,
} from '../course-external-link';

// Schema definition for course creation
export const createCourseSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Názov kurzu
    description: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Popis kurzu
    content: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Obsah kurzu
    targetAudience: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Pre koho je určený kurz
    courseType: z.array(z.any().nullable().optional()), // Forma potvrdenia po absolvovaní kurzu
    confirmationForm: z.array(z.any()).min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Forma potvrdenia po absolvovaní kurzu
    learningMethod: z.array(z.any()).min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Spôsob vzdelávania
    instructors: z
      .array(
        z.object({
          name: z.any(),
        }),
      )
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Lektorovia
    startDate: z.any().optional(), // Začiatok kurzu
    endDate: z.any().optional(), // Koniec kurzu
    price: z
      .string()
      .trim()
      .min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE())
      .regex(/^\d+(?:[.,]\d{1,2})?$/, VM.INVALID_DECIMAL_VALIDATION_MESSAGE())
      .refine(
        value => Number.isFinite(Number(value.replace(',', '.'))),
        VM.INVALID_NUMBER_VALIDATION_MESSAGE(),
      ),
    externalUrl: z
      .string()
      .trim()
      .refine(
        value => !value || !!normalizeCourseExternalUrlInput(value),
        COURSE_EXTERNAL_URL_ERROR,
      ),
    canBeReimbursed: z.boolean(), // Možnosť preplatiť úradom práce
    requiresExam: z.boolean(), // Potrebná skúška odbornej spôsobilosti
    image: z.any().nullable(),
    categories: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Kategórie kurzu
    courseLocation: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Lokácia kurzu
  });

type CreateCourseFormValues = z.infer<ReturnType<typeof createCourseSchema>>;

export const CreateCourseForm = () => {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(state => state.currentLocale);
  const { toast } = useToast();

  const { me, isInitialized } = useCustomerStore(state => state);
  const router = useRouter();
  const navigateToProfile = useProfileNavigation();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!me || !me.customer || me.customer.customer_type?.type !== 'school') {
      router.push('/');
    }
  }, [isInitialized, me, router]);

  const form = useForm<CreateCourseFormValues>({
    mode: 'onChange',
    resolver: zodResolver(createCourseSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      image: null,
      description: '',
      content: '',
      targetAudience: '',
      confirmationForm: [],
      learningMethod: [],
      instructors: [{ name: '' }],
      courseType: [],
      categories: '',
      startDate: null,
      endDate: null,
      price: '',
      externalUrl: '',
      canBeReimbursed: false,
      requiresExam: false,
      courseLocation: '',
    } as any,
  });

  const [currentStep, setCurrentStep] = React.useState(0);
  const [currentErrorStep, setCurrentErrorStep] = React.useState<
    keyof typeof AddCourseStepsBuckets | null
  >(null);

  const checkIfStepInputsValid = async (step: keyof typeof AddCourseStepsBuckets) => {
    const currentBucket = AddCourseStepsBuckets[step];
    if (!currentBucket) return true;

    const isValid = await form.trigger(currentBucket as any, {
      shouldFocus: true,
    });

    const hasErrors = currentBucket.some(field => {
      const error = deepGet(form.formState.errors, field);
      return !!error;
    });

    if (!isValid || hasErrors) {
      setCurrentErrorStep(step);
      return false;
    }
    setCurrentErrorStep(null);
    return true;
  };

  const handleSetCurrentStep = async (step: number) => {
    const isValid = await checkIfStepInputsValid(currentStep as keyof typeof AddCourseStepsBuckets);
    if (isValid) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = form.handleSubmit(
    async data => {
      if (data.image) {
        const formData = new FormData();

        formData.append('file', data.image);

        try {
          const res = await uploadFile({
            authToken: me?.authToken,
            formData,
            locale,
          });
          data.image = res.data as unknown as File;
        } catch (e) {
          return;
        }
      }

      const transformedData = {
        name: data.name,
        description: data.description,
        short_description: data.content,
        date_start: data.startDate ?? null,
        date_stop: data.endDate ?? null,
        price_with_vat: Number(data.price.replace(',', '.')),
        text_fields: buildCourseTextFields([], data.externalUrl),
        image: data.image,
        categories: [
          {
            id: parseInt(data.categories, 10),
          },
        ],
        metadata: {
          instructors: data.instructors?.map(instructor => {
            if (!instructor.name) {
              return undefined;
            }
            return {
              name: instructor.name,
            };
          }),
          canBeReimbursed: data.canBeReimbursed,
          requiresExam: data.requiresExam,
          confirmationForm: data.confirmationForm.length ? data.confirmationForm : [],
          learningMethod: data.learningMethod.length ? data.learningMethod : [],
          targetAudience: data.targetAudience,
          courseType: data.courseType?.map(value => {
            return {
              id: value,
              name: value,
            };
          }),
          agencyName: me?.customer?.name,
          courseLocation: data.courseLocation,
        },
      };

      await createCourse({ locale, body: transformedData, authToken: me?.authToken })
        .then(() => {
          toast({
            title: 'Kurz bol úspešne vytvorený',
            description:
              'Pred zverejnením vzdelávacieho kurzu je potrebné ho zaplatiť a schváliť administrátorom.',
            variant: 'success',
          });
          navigateToProfile(ROUTES.PROFILE() + '?initStep=school-courses');
        })
        .catch(e => {
          if (e instanceof ValidationError) {
            for (const [field, errors] of Object.entries(e.fields)) {
              const formField = field.startsWith('text_fields')
                ? 'externalUrl'
                : getCourseFormErrorField(field);
              form.setError(formField as any, { type: 'manual', message: errors.join('\n') });
            }
            setCurrentStep(0);
          }
          toast({
            title: t('text1472'),
            description:
              (e instanceof ValidationError && Object.values(e.fields).flat().join(' ')) ||
              'Skontrolujte prosím zadané údaje a skúste to znova.',
            variant: 'destructive',
          });
        });
    },
    () => {},
  );

  if (!isInitialized || !me || !me.customer || me.customer.customer_type?.type !== 'school') {
    return <PortalContentLoader />;
  }

  return (
    <FormProvider {...form}>
      <form
        className={cn('flex w-full items-start justify-start gap-2 py-4', 'ea-create-course-form')}
        onSubmit={handleSubmit}
      >
        <AddCourseStepper
          currentStep={currentStep}
          setCurrentStep={handleSetCurrentStep}
          currentErrorStep={currentErrorStep}
        />
        <div className="flex w-full flex-col gap-4">
          {currentStep === 0 && <AddCourseStep0 />}
          <AddCourseControls handleSubmit={handleSubmit} />
        </div>
      </form>
    </FormProvider>
  );
};
